import { apiClient, type ManualCommandConfig } from '@/services/http/client'
import { digest, id, object, scopedActions, type Data } from './workflow'

export type BusinessKind =
  | 'review'
  | 'prepare'
  | 'issue'
  | 'cancel'
  | 'reconcile'
  | 'project'
  | 'revoke'
export interface BusinessReference {
  kind: BusinessKind
  target: string
  command: string
  status: 'pending' | 'confirmed'
  preparation?: { review: string; acceptance: string; decision: string }
  reason?: 'operator_cancelled' | 'not_eligible' | 'withdrawn'
}
const kinds: BusinessKind[] = [
  'review',
  'prepare',
  'issue',
  'cancel',
  'reconcile',
  'project',
  'revoke',
]
const base = '/legal-control-policies'
const admission = `${base}/business-admissions`
const key = (reviewer: string) => 'taxhive.business-legal-workflow.v1:' + id(reviewer)
function reference(value: unknown): BusinessReference {
  const r = object(value)
  if (
    Object.keys(r).some(
      k => !['kind', 'target', 'command', 'status', 'preparation', 'reason'].includes(k)
    ) ||
    !kinds.includes(r.kind as BusinessKind) ||
    (r.status !== 'pending' && r.status !== 'confirmed')
  )
    throw new Error('Original business history unavailable')
  const result: BusinessReference = {
    kind: r.kind as BusinessKind,
    target: id(r.target),
    command: id(r.command),
    status: r.status as BusinessReference['status'],
  }
  if (r.preparation !== undefined) {
    const p = object(r.preparation)
    if (r.kind !== 'prepare' || Object.keys(p).sort().join(',') !== 'acceptance,decision,review')
      throw new Error('Exact business preparation references required')
    result.preparation = {
      review: id(p.review),
      acceptance: id(p.acceptance),
      decision: id(p.decision),
    }
  }
  if (r.reason !== undefined) {
    if (
      r.kind !== 'cancel' ||
      !['operator_cancelled', 'not_eligible', 'withdrawn'].includes(r.reason as string)
    )
      throw new Error('Exact original no-effect reason required')
    result.reason = r.reason as BusinessReference['reason']
  }
  if (r.kind === 'prepare' && !result.preparation)
    throw new Error('Original business preparation unavailable')
  return result
}
export function businessHistory(reviewer: string): BusinessReference[] {
  const raw = localStorage.getItem(key(reviewer))
  if (!raw) return []
  const rows: unknown = JSON.parse(raw)
  if (!Array.isArray(rows) || rows.length > 100)
    throw new Error('Original business history unavailable')
  return rows.map(reference)
}
export function saveBusinessReference(reviewer: string, value: BusinessReference): void {
  const ref = reference(value),
    rows = businessHistory(reviewer)
  const prior = rows.find(r => r.kind === ref.kind && r.command === ref.command)
  if (
    prior &&
    (prior.target !== ref.target ||
      (prior.kind === 'cancel' &&
        (prior.reason ?? 'operator_cancelled') !== (ref.reason ?? 'operator_cancelled')) ||
      JSON.stringify(prior.preparation) !== JSON.stringify(ref.preparation))
  )
    throw new Error('Original business command differs')
  if (rows.some(r => r.command === ref.command && r.target !== ref.target))
    throw new Error('Original business target differs')
  const rest = rows.filter(r => !(r.kind === ref.kind && r.command === ref.command))
  const updated = [
    ref,
    ...rest.filter(r => r.status === 'pending'),
    ...rest.filter(r => r.status === 'confirmed').slice(0, 20),
  ]
  if (updated.length > 100) throw new Error('Recover business history before creating a command')
  const encoded = JSON.stringify(updated)
  localStorage.setItem(key(reviewer), encoded)
  if (localStorage.getItem(key(reviewer)) !== encoded)
    throw new Error('Original business references were not saved')
}
export function businessEndpoint(value: BusinessReference, recovery = false): string {
  const r = reference(value)
  if (r.kind === 'review')
    return recovery
      ? `${base}/reviews/${r.command}`
      : `${base}/business-review-intakes/${r.target}/reviews`
  if (r.kind === 'prepare')
    return recovery ? `${admission}/${r.command}` : `${admission}/intakes/${r.target}/prepare`
  if (r.kind === 'project' || r.kind === 'revoke')
    return (
      `${admission}/${r.target}/` +
      (recovery || r.kind === 'project' ? 'controller' : 'revoke-controller')
    )
  return (
    `${admission}/${r.target}` + (recovery ? '' : r.kind === 'reconcile' ? '/reconcile' : '/decide')
  )
}
export function businessPreparationBody(ref: BusinessReference): Data {
  const r = reference(ref),
    p = r.preparation
  if (r.kind !== 'prepare' || !p) throw new Error('Original preparation unavailable')
  return {
    request_id: r.target,
    intent_id: r.command,
    decision_id: p.decision,
    review_id: p.review,
    acceptance_id: p.acceptance,
  }
}
function exactKeys(body: Data, keys: string) {
  if (Object.keys(body).sort().join(',') !== keys.split(',').sort().join(','))
    throw new Error('Exact business command only')
}
export function validateBusinessCommand(ref: BusinessReference, body: Data): void {
  const r = reference(ref)
  if (r.kind === 'prepare') {
    const expected = businessPreparationBody(r)
    exactKeys(body, Object.keys(expected).join(','))
    if (Object.keys(expected).some(k => body[k] !== expected[k]))
      throw new Error('Original preparation differs')
  } else if (r.kind === 'issue' || r.kind === 'cancel') {
    exactKeys(body, 'intent_id,decision_id,action,reason')
    if (
      id(body.intent_id) !== r.target ||
      id(body.decision_id) !== r.command ||
      body.action !== (r.kind === 'issue' ? 'issued' : 'no_effect') ||
      body.reason !== (r.kind === 'issue' ? null : (r.reason ?? 'operator_cancelled'))
    )
      throw new Error('Original decision differs')
  } else if (r.kind === 'project' || r.kind === 'revoke') {
    exactKeys(body, r.kind === 'project' ? 'intent_id,command_id' : 'intent_id,command_id,reason')
    if (
      id(body.intent_id) !== r.target ||
      id(body.command_id) !== r.command ||
      (r.kind === 'revoke' &&
        (typeof body.reason !== 'string' ||
          body.reason.trim().length < 8 ||
          body.reason.length > 512))
    )
      throw new Error('Original controller command differs')
  } else if (r.kind === 'reconcile') exactKeys(body, '')
  else {
    exactKeys(
      body,
      'review_id,expected_ordinal,policy_id,policy_body_sha256,rule_id,authority_seconds,policy_evidence_sha256,reviewed_evidence,decision,decision_record_sha256,expires_at,attest_policy_document_and_evidence_reviewed,acknowledge_no_controller_authority'
    )
    if (id(body.review_id) !== r.command || body.expected_ordinal !== 1)
      throw new Error('Original review differs')
  }
}
export function businessCancellationBody(value: BusinessReference): Data {
  const r = reference(value)
  if (r.kind !== 'cancel') throw new Error('Original no-effect command required')
  return {
    intent_id: r.target,
    decision_id: r.command,
    action: 'no_effect',
    reason: r.reason ?? 'operator_cancelled',
  }
}
export async function postBusiness(ref: BusinessReference, body: Data): Promise<unknown> {
  validateBusinessCommand(ref, body)
  const r = reference(ref)
  return (
    await apiClient.post(businessEndpoint(r), r.kind === 'reconcile' ? undefined : body, {
      manualCommand: true,
    } as ManualCommandConfig)
  ).data
}
export async function recoverBusiness(ref: BusinessReference): Promise<unknown> {
  return (await apiClient.get(businessEndpoint(ref, true))).data
}
export function businessReceipt(value: unknown, ref: BusinessReference, reviewer: string): Data {
  const r = reference(ref),
    row = object(value)
  if (r.kind === 'review') {
    if (
      id(row.review_id) !== r.command ||
      id(row.reviewer_user_id) !== id(reviewer) ||
      row.authority !== 'historical_human_review_only' ||
      row.authority_granted !== false ||
      row.controller_admitted !== false ||
      typeof row.terms_sha256 !== 'string' ||
      !digest.test(row.terms_sha256)
    )
      throw new Error('Original business review unavailable')
    return row
  }
  if (r.kind === 'project' || r.kind === 'revoke') {
    if (
      row.authority !== 'governed_business_controller_projection' ||
      id(row.intent_id) !== r.target ||
      (row.role !== 'legal_controller' && row.role !== 'representative') ||
      typeof row.state !== 'string' ||
      !['active', 'expired', 'revoked', 'lapsed'].includes(row.state) ||
      (r.kind === 'project' && id(row.command_id) !== r.command) ||
      (r.kind === 'revoke' &&
        (row.state !== 'revoked' || id(row.revocation_command_id) !== r.command))
    )
      throw new Error('Original business appointment unavailable')
    id(row.client_id)
    id(row.account_id)
    scopedActions(row.actions)
    if (row.state === 'revoked') {
      id(row.revocation_command_id)
      if (typeof row.revoked_at !== 'string' || !Number.isFinite(Date.parse(row.revoked_at)))
        throw new Error('Revocation receipt unavailable')
    } else if (row.revocation_command_id !== null || row.revoked_at !== null)
      throw new Error('Exact revocation state required')
    if ((row.state === 'lapsed') !== (row.controller_id === null))
      throw new Error('Exact lapsed disposition required')
    if (row.controller_id !== null) id(row.controller_id)
    if (
      typeof row.issued_at !== 'string' ||
      typeof row.expires_at !== 'string' ||
      !Number.isFinite(Date.parse(row.issued_at)) ||
      !Number.isFinite(Date.parse(row.expires_at)) ||
      Date.parse(row.expires_at) <= Date.parse(row.issued_at)
    )
      throw new Error('Exact finite appointment required')
    return row
  }
  const command = object(row.command),
    source = object(command.source)
  if (
    id(command.intent_id) !== (r.kind === 'prepare' ? r.command : r.target) ||
    id(row.decision_id) !== (r.kind === 'prepare' ? r.preparation!.decision : r.command) ||
    id(row.reviewer_id) !== id(reviewer) ||
    row.authority_granted !== false ||
    row.controller_admitted !== false ||
    command.schema_version !== 2 ||
    typeof command.terms_sha256 !== 'string' ||
    !digest.test(command.terms_sha256)
  )
    throw new Error('Original business intent unavailable')
  id(command.review_id)
  id(command.acceptance_id)
  id(command.consent_id)
  id(source.request_id)
  if (
    r.kind === 'prepare' &&
    (id(source.request_id) !== r.target ||
      id(command.review_id) !== r.preparation!.review ||
      id(command.acceptance_id) !== r.preparation!.acceptance)
  )
    throw new Error('Original accepted request differs')
  const states = [
    'reserved',
    'dispatch_pending',
    'cancelled',
    'no_effect',
    'issued_pending_projection',
    'issued_projected',
    'issued_lapsed',
  ]
  if (
    typeof row.state !== 'string' ||
    !states.includes(row.state) ||
    row.source_held !==
      ['reserved', 'dispatch_pending', 'issued_pending_projection'].includes(String(row.state))
  )
    throw new Error('Exact business source disposition required')
  if (r.kind !== 'prepare' && ['reserved', 'dispatch_pending'].includes(String(row.state)))
    throw new Error('Decision outcome remains unconfirmed;reconcile the original intent')
  return row
}
export async function inspectBusiness(request: string): Promise<Data> {
  const selected = id(request),
    row = object(
      (
        await apiClient.post(`${base}/business-review-intakes/${selected}/inspect`, undefined, {
          manualCommand: true,
        } as ManualCommandConfig)
      ).data
    )
  const command = object(row.command),
    source = object(row.source),
    subject = object(row.subject)
  if (
    row.source_kind !== 'firm_business_customer' ||
    row.authority !== 'business_review_source_observation_only' ||
    [
      row.authority_granted,
      row.reservation_held,
      row.legal_subject_verified,
      row.evidence_verified,
    ].some(v => v !== false) ||
    id(command.request_id) !== selected ||
    id(subject.client_id) !== id(command.client_id) ||
    (command.requested_role !== 'legal_controller' && command.requested_role !== 'representative')
  )
    throw new Error('Current business source unavailable')
  id(command.proposed_account_id)
  id(subject.subject_id)
  scopedActions(command.requested_actions)
  for (const k of [
    'client_id',
    'alias_id',
    'source_epoch',
    'customer_id',
    'source_revision',
    'source_sha256',
  ])
    if (command[k] !== source[k]) throw new Error('Business source differs')
  if (
    !Array.isArray(command.declared_evidence) ||
    !command.declared_evidence.length ||
    command.declared_evidence.length > 10
  )
    throw new Error('Declared evidence unavailable')
  for (const value of command.declared_evidence) {
    const e = object(value)
    id(e.evidence_id)
    if (typeof e.digest !== 'string' || !digest.test(e.digest))
      throw new Error('Exact evidence fingerprint required')
  }
  return row
}

export async function inspectBusinessIntent(
  intent: string,
  reviewer: string
): Promise<{ ref: BusinessReference; row: Data }> {
  const target = id(intent),
    row = object((await apiClient.get(`${admission}/${target}`)).data)
  const command = object(row.command),
    source = object(command.source)
  const ref: BusinessReference = {
    kind: 'prepare',
    target: id(source.request_id),
    command: target,
    status: 'confirmed',
    preparation: {
      review: id(command.review_id),
      acceptance: id(command.acceptance_id),
      decision: id(row.decision_id),
    },
  }
  return { ref, row: businessReceipt(row, ref, reviewer) }
}

export async function inspectBusinessController(
  intent: string,
  reviewer: string
): Promise<{ ref: BusinessReference; row: Data }> {
  const target = id(intent),
    row = object((await apiClient.get(`${admission}/${target}/controller`)).data)
  const ref: BusinessReference = {
    kind: 'project',
    target,
    command: id(row.command_id),
    status: 'confirmed',
  }
  return { ref, row: businessReceipt(row, ref, reviewer) }
}
